import React, { useState, useRef, useEffect } from "react";
import { 
  List, 
  Space, 
  Divider, 
  Input, 
  Button, 
  Card, 
  Avatar, 
  Typography,
  Row,
  Col,
  message,
  Image
} from 'antd';
import {
  PlusOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  DeleteOutlined,
  LikeOutlined,
  DislikeOutlined
} from '@ant-design/icons';
import OpenAIService from '../../services/OpenAIService';
import FormattedBotMessage from './FormattedBotMessage';

const { TextArea } = Input;
const { Text } = Typography;

 

function GPT(props) {
  const [chats, setChats] = useState(() => {
    const saved = localStorage.getItem('chatHistory');
    return saved ? JSON.parse(saved) : [
      { id: 1, title: "Field Analysis Help", lastMessage: "How can I analyze crop health?", timestamp: "2024-01-15" },
      { id: 2, title: "Weather Data Query", lastMessage: "What's the weather forecast?", timestamp: "2024-01-14" }
    ];
  });
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [feedbackInput, setFeedbackInput] = useState({});
  const [messageFeedback, setMessageFeedback] = useState({});
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createNewChat = () => {
    const newChat = {
      id: Date.now(),
      title: "New Chat",
      lastMessage: "",
      timestamp: new Date().toISOString().split('T')[0]
    };
    const updatedChats = [newChat, ...chats];
    setChats(updatedChats);
    localStorage.setItem('chatHistory', JSON.stringify(updatedChats));
    setActiveChat(newChat.id);
    setMessages([]);
    localStorage.setItem(`chatMessages_${newChat.id}`, JSON.stringify([]));
  };

  const selectChat = (chatId) => {
    setActiveChat(chatId);
    const savedMessages = localStorage.getItem(`chatMessages_${chatId}`);
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      const welcomeMessages = [
        { id: 1, text: "Hello! How can I help you today?", sender: "bot", timestamp: new Date(), needsFeedback: true }
      ];
      setMessages(welcomeMessages);
      localStorage.setItem(`chatMessages_${chatId}`, JSON.stringify(welcomeMessages));
    }
    setFeedbackInput({});
    setMessageFeedback({});
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeChat) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date()
    };

    const currentInput = inputMessage;
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    localStorage.setItem(`chatMessages_${activeChat}`, JSON.stringify(updatedMessages));
    setInputMessage("");
    setIsLoading(true);

    try {
      const aiResponse = await OpenAIService.sendMessage(updatedMessages);
      
      const botMessage = {
        id: Date.now() + 1,
        text: aiResponse,
        sender: "bot",
        timestamp: new Date(),
        needsFeedback: true
      };
      
      const finalMessages = [...updatedMessages, botMessage];
      setMessages(finalMessages);
      localStorage.setItem(`chatMessages_${activeChat}`, JSON.stringify(finalMessages));
      
      const updatedChats = chats.map(chat => 
        chat.id === activeChat 
          ? { 
              ...chat, 
              title: currentInput.slice(0, 30) + (currentInput.length > 30 ? "..." : ""), 
              lastMessage: aiResponse.slice(0, 50) + (aiResponse.length > 50 ? "..." : "")
            }
          : chat
      );
      setChats(updatedChats);
      localStorage.setItem('chatHistory', JSON.stringify(updatedChats));
    } catch (error) {
      message.error(error.message || 'Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    const updatedChats = chats.filter(chat => chat.id !== chatId);
    setChats(updatedChats);
    localStorage.setItem('chatHistory', JSON.stringify(updatedChats));
    localStorage.removeItem(`chatMessages_${chatId}`);
    if (activeChat === chatId) {
      setActiveChat(null);
      setMessages([]);
    }
  };

  const handleFeedback = (messageId, isPositive) => {
    const feedback = {
      messageId,
      isPositive,
      timestamp: new Date().toISOString(),
      chatId: activeChat
    };
    
    // Store in localStorage
    const existingFeedback = JSON.parse(localStorage.getItem('chatFeedback') || '[]');
    existingFeedback.push(feedback);
    localStorage.setItem('chatFeedback', JSON.stringify(existingFeedback));
    
    setMessageFeedback(prev => ({ ...prev, [messageId]: isPositive }));
    
    if (!isPositive) {
      setFeedbackInput(prev => ({ ...prev, [messageId]: '' }));
    }
  };

  const submitFeedbackText = (messageId) => {
    const feedbackText = feedbackInput[messageId];
    if (!feedbackText?.trim()) return;
    
    const detailedFeedback = {
      messageId,
      isPositive: false,
      feedbackText: feedbackText.trim(),
      timestamp: new Date().toISOString(),
      chatId: activeChat
    };
    
    // Update localStorage
    const existingFeedback = JSON.parse(localStorage.getItem('chatFeedback') || '[]');
    const updatedFeedback = existingFeedback.map(f => 
      f.messageId === messageId ? { ...f, feedbackText: feedbackText.trim() } : f
    );
    localStorage.setItem('chatFeedback', JSON.stringify(updatedFeedback));
    
    // Clear input
    setFeedbackInput(prev => ({ ...prev, [messageId]: undefined }));
    message.success('Feedback submitted successfully');
  };

  const renderMessageContent = (message) => {
    const showText = message.text ? (
      message.sender === "bot" ? <FormattedBotMessage text={message.text} /> : message.text
    ) : null;

    if (!message.image) {
      return showText;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <Image src={message.image} width={140} preview={false} />
        {showText}
      </div>
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Divider orientation="left" style={{ color: "#282c34", margin: "16px 0 8px 0" }}>
        AI Chat Support
      </Divider>
      
      <Row style={{ height: "calc(100% - 60px)" }}>
        {/* Chat History Sidebar */}
        <Col span={10} style={{ borderRight: "1px solid #f0f0f0", height: "100%" }}>
          <div style={{ padding: "8px" }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={createNewChat}
              style={{ width: "100%", marginBottom: "8px" }}
              size="small"
            >
              New Chat
            </Button>
            
            <List
              dataSource={chats}
              size="small"
              style={{ height: "calc(100% - 40px)", overflowY: "auto" }}
              renderItem={(chat) => (
                <List.Item
                  style={{
                    cursor: "pointer",
                    backgroundColor: activeChat === chat.id ? "#e6f7ff" : "transparent",
                    borderRadius: "4px",
                    margin: "2px 0",
                    padding: "8px"
                  }}
                  onClick={() => selectChat(chat.id)}
                >
                  <div style={{ width: "100%", position: "relative" }}>
                    <Text strong style={{ fontSize: "12px", display: "block" }}>
                      {chat.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: "10px" }}>
                      {chat.lastMessage}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => deleteChat(chat.id, e)}
                      style={{ 
                        position: "absolute", 
                        top: "0", 
                        right: "0",
                        fontSize: "10px"
                      }}
                    />
                  </div>
                </List.Item>
              )}
            />
          </div>
        </Col>

        {/* Chat Interface */}
        <Col span={14} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {activeChat ? (
            <>
              {/* Messages Area */}
              <div style={{ 
                flex: 1, 
                overflowY: "auto", 
                padding: "8px",
                backgroundColor: "#fafafa"
              }}>
                {messages.map((message) => (
                  <div key={message.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: message.sender === "user" ? "flex-end" : "flex-start",
                        marginBottom: "8px"
                      }}
                    >
                      <Card
                        size="small"
                        style={{
                          maxWidth: "80%",
                          backgroundColor: message.sender === "user" ? "#1890ff" : "#fff",
                          color: message.sender === "user" ? "#fff" : "#000"
                        }}
                        bodyStyle={{ padding: "8px 12px" }}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <Avatar 
                            size={24}
                            icon={message.sender === "user" ? <UserOutlined /> : <RobotOutlined />}
                            style={{ 
                              backgroundColor: message.sender === "user" ? "#fff" : "#1890ff",
                              color: message.sender === "user" ? "#1890ff" : "#fff",
                              flexShrink: 0
                            }}
                          />
                          <div style={{ 
                            color: message.sender === "user" ? "#fff" : "#000",
                            fontSize: "12px",
                            lineHeight: "1.4",
                            wordBreak: "break-word"
                          }}>
                            {renderMessageContent(message)}
                          </div>
                        </div>
                      </Card>
                    </div>
                    
                    {/* Feedback UI for bot messages */}
                    {message.sender === "bot" && message.needsFeedback && (
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "flex-start", 
                        marginBottom: "12px",
                        marginLeft: "8px"
                      }}>
                        <Space size="small">
                          <Button
                            size="small"
                            type={messageFeedback[message.id] === true ? "primary" : "default"}
                            icon={<LikeOutlined />}
                            onClick={() => handleFeedback(message.id, true)}
                            style={{ fontSize: "10px" }}
                          />
                          <Button
                            size="small"
                            type={messageFeedback[message.id] === false ? "primary" : "default"}
                            icon={<DislikeOutlined />}
                            onClick={() => handleFeedback(message.id, false)}
                            style={{ fontSize: "10px" }}
                          />
                        </Space>
                      </div>
                    )}
                    
                    {/* Feedback input for negative feedback */}
                    {feedbackInput[message.id] !== undefined && (
                      <div style={{ 
                        marginBottom: "12px", 
                        marginLeft: "8px",
                        maxWidth: "80%"
                      }}>
                        <Space.Compact style={{ width: "100%" }}>
                          <Input
                            size="small"
                            placeholder="What could be improved?"
                            value={feedbackInput[message.id]}
                            onChange={(e) => setFeedbackInput(prev => ({
                              ...prev,
                              [message.id]: e.target.value
                            }))}
                            onPressEnter={() => submitFeedbackText(message.id)}
                            style={{ fontSize: "11px" }}
                          />
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => submitFeedbackText(message.id)}
                            disabled={!feedbackInput[message.id]?.trim()}
                          >
                            Send
                          </Button>
                        </Space.Compact>
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "8px" }}>
                    <Card size="small" bodyStyle={{ padding: "8px 12px" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>AI is typing...</Text>
                    </Card>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
                <Space.Compact style={{ width: "100%" }}>
                  <TextArea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  <Button 
                    type="primary" 
                    icon={<SendOutlined />}
                    onClick={sendMessage}
                    loading={isLoading}
                    disabled={!inputMessage.trim()}
                  />
                </Space.Compact>
              </div>
            </>
          ) : (
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              height: "100%",
              flexDirection: "column",
              color: "#999"
            }}>
              <RobotOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
              <Text type="secondary">Select a chat or create a new one to start</Text>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default GPT;
